import type { CommandPaletteGroup, CommandPaletteItem } from '@nuxt/ui';
import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { RouteLocationNormalizedLoaded, RouteLocationRaw } from 'vue-router';
import type { FoldableBreadcrumbItem } from '#layers/ui/types/breadcrumb-foldable';
import { computed, toValue } from 'vue';
import { useTranslationUtils } from '#layers/i18n/public';

interface BreadcrumbPage {
	id: string;
	label: string;
	icon?: string;
	to: RouteLocationRaw;
	/** Must also match nested detail routes, so the page stays current there. */
	matches: (route: RouteLocationNormalizedLoaded) => boolean;
}

export interface BreadcrumbPageGroup {
	id: string;
	label?: string;
	items: BreadcrumbPage[];
}

interface UseBreadcrumbPageNavigationOptions {
	groups: MaybeRefOrGetter<BreadcrumbPageGroup[]>;
	route: RouteLocationNormalizedLoaded;
	/** Slot name the consumer renders `BreadcrumbPageSwitcher` in. */
	slot: string;
	/** Label for the current page while no listed page matches the route. Defaults to the route's `page` title. */
	fallbackLabel?: MaybeRefOrGetter<string | undefined>;
}

interface UseBreadcrumbPageNavigationReturn {
	pageItem: ComputedRef<FoldableBreadcrumbItem | null>;
	pageLabel: ComputedRef<string>;
	/** The pages as `BreadcrumbPageSwitcher` groups, with the current page marked active. */
	commandGroups: ComputedRef<CommandPaletteGroup<CommandPaletteItem>[]>;
}

export function useBreadcrumbPageNavigation({ groups, route, slot, fallbackLabel }: UseBreadcrumbPageNavigationOptions): UseBreadcrumbPageNavigationReturn {
	const pageGroups = computed(() => toValue(groups));
	const routePageTitle = useRoutePageTitle(route);

	const pageItem = computed<FoldableBreadcrumbItem | null>(() => {
		const current = findCurrentPage(pageGroups.value, route);
		const label = current?.label ?? toValue(fallbackLabel ?? routePageTitle);

		if (label === undefined) return null;

		if (countPages(pageGroups.value) >= 2) {
			return { label, slot, switcher: true };
		}

		return current ? { label, to: current.to } : { label };
	});

	const pageLabel = computed(() => pageItem.value?.label ?? '');

	const commandGroups = computed(() => pageGroups.value.map((group): CommandPaletteGroup<CommandPaletteItem> => ({
		id: group.id,
		label: group.label,
		items: group.items.map((page) => ({
			label: page.label,
			icon: page.icon,
			active: page.matches(route),
			to: page.to,
		})),
	})));

	return { pageItem, pageLabel, commandGroups };
}

function useRoutePageTitle(route: RouteLocationNormalizedLoaded) {
	const { translateString } = useTranslationUtils();

	return () => {
		const title = route.matched.at(-1)?.meta.page?.title;
		return title ? translateString(title) : undefined;
	};
}

function findCurrentPage(groups: BreadcrumbPageGroup[], route: RouteLocationNormalizedLoaded): BreadcrumbPage | undefined {
	for (const group of groups) {
		const page = group.items.find((item) => item.matches(route));
		if (page) return page;
	}

	return undefined;
}

function countPages(groups: BreadcrumbPageGroup[]): number {
	return groups.reduce((count, group) => count + group.items.length, 0);
}
